import { Header, Spinner } from 'view/components';
import { useEffect, useState } from 'react';
import { NoResultsBlock } from 'view/pages/candidates/components';
import * as CS from 'view/pages/candidates/commonStyled';
import {
  JOB_POSTS_ITEMS_PER_PAGE,
  useGetActiveOrDraftJobPosts,
} from 'view/pages/candidates/hooks/useGetActiveOrDraftJobPosts';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { EditGreyIcon, DraftGreyIcon, DeleteRedIcon, DraftRestoreGreyIcon } from 'assets';
import { DeleteJobPostModal, MoveJobPostToActiveModal, MoveJobPostToDraftModal } from 'view/modals';
import { useContextSelector as useCS } from 'use-context-selector';
import {
  GlobalConfigContext,
  TGlobalConfigContextState,
} from 'domain/globalConfig/GlobalConfigContext';
import { globalConfigSelectors } from 'domain/globalConfig/selectors';
import { TUpdate } from 'domain/types';
import { storageService } from 'infrastructure/services/StorageService';
import { useUpdateJobPostByStep } from 'view/pages/createJobPost/hooks/useUpdateJobPostByStep';
import { useNavigate } from 'react-router';
import { showSystemToast } from 'view/components/SystemToast';
import { useStateMachine } from 'little-state-machine';
import { resetForm } from 'view/pages/createJobPost/helpers/resetForm';
import * as S from './styled';

const MyJobPostsPage = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [draftsPage, setDraftsPage] = useState(1);

  const params = new URLSearchParams(window.location.search);
  const postStatusFromUrl = params.get('postStatus');

  const update: TUpdate<TGlobalConfigContextState> | undefined = useCS(
    GlobalConfigContext,
    globalConfigSelectors.update
  );

  const { actions } = useStateMachine({
    actions: { resetForm },
  });

  const {
    isLoading: isActiveJobPostsLoading,
    getActiveJobPosts,
    data: activeJobPostsData,
  } = useGetActiveOrDraftJobPosts();

  const {
    isLoading: isDraftsJobPostsLoading,
    getDraftsJobPosts,
    data: draftsJobPostsData,
  } = useGetActiveOrDraftJobPosts();

  const { updateJobPostForEditing } = useUpdateJobPostByStep('/create-job-post/step1');

  useEffect(() => {
    getActiveJobPosts();
    getDraftsJobPosts();
  }, []);

  const updateUrl = (status) => {
    const params = new URLSearchParams(window.location.search);
    params.set('postStatus', status);
    navigate({
      pathname: window.location.pathname,
      search: `?${params.toString()}`,
    });
  };

  const deleteActivePostSuccessFunction = () => {
    if (activeJobPostsData?.results?.length === 1 && page !== 1) {
      getActiveJobPosts(page - 1);
      setPage(page - 1);
      return;
    }
    getActiveJobPosts(page);
    showSystemToast(
      'success',
      'Job post deleted successfully.',
      'This job is no longer visible to job seekers.'
    );
  };

  const deleteDraftPostSuccessFunction = () => {
    if (draftsJobPostsData?.results?.length === 1 && draftsPage !== 1) {
      getDraftsJobPosts(draftsPage - 1);
      setDraftsPage(draftsPage - 1);
      return;
    }
    getDraftsJobPosts(draftsPage);
    showSystemToast(
      'success',
      'Job post deleted successfully.',
      'This job is no longer visible to job seekers.'
    );
  };

  const moveJobPostToDraftSuccessFunction = () => {
    if (activeJobPostsData?.results?.length === 1 && page !== 1) {
      getActiveJobPosts(page - 1);
      setPage(page - 1);
      getDraftsJobPosts(1);
      setDraftsPage(1);
      return;
    }
    getActiveJobPosts(page);
    getDraftsJobPosts(1);
    setDraftsPage(1);
    showSystemToast(
      'success',
      'Job post moved to draft.',
      'It’s now hidden from job seekers. You can edit and repost it anytime.'
    );
  };

  const moveJobPostToActiveSuccessFunction = () => {
    if (draftsJobPostsData?.results?.length === 1 && draftsPage !== 1) {
      getDraftsJobPosts(draftsPage - 1);
      setDraftsPage(draftsPage - 1);
      getActiveJobPosts(1);
      setPage(1);
      return;
    }
    getDraftsJobPosts(draftsPage);
    getActiveJobPosts(1);
    setPage(1);
    showSystemToast(
      'success',
      'Job post moved to active.',
      'It’s now shown for job seekers. You can edit and repost it anytime.'
    );
  };

  return (
    <S.Wrapper>
      <Header />
      <S.TabsContainer>
        <S.TabItem
          selected={postStatusFromUrl === 'active'}
          onClick={() => {
            updateUrl('active');
          }}
        >
          <p>Active Posts ({activeJobPostsData?.total || 0})</p>
        </S.TabItem>
        <S.TabItem
          selected={postStatusFromUrl === 'draft'}
          onClick={() => {
            updateUrl('draft');
          }}
        >
          <p>Drafts ({draftsJobPostsData?.total || 0})</p>
        </S.TabItem>
      </S.TabsContainer>
      {postStatusFromUrl === 'active' && (
        <S.MainContentWrapper>
          {isActiveJobPostsLoading && (
            <Spinner
              variant="outlined"
              style={{
                position: 'absolute',
                top: '0',
                left: '0',
                backgroundColor: 'rgba(217, 225, 255, 0.3)',
                width: '100%',
                height: '100%',
                zIndex: 10,
              }}
            />
          )}
          {!isActiveJobPostsLoading && !activeJobPostsData?.total && (
            <NoResultsBlock
              title="🕵️‍♂️ No Jobs Found Here"
              subTitle={
                'Jobs you post will appear here. Start by creating \n your first job post to find great candidates.'
              }
              withButton
            />
          )}
          {activeJobPostsData?.total && !!activeJobPostsData?.results?.length ? (
            <>
              <S.CandidatesListBlockMainWrapper>
                <Table sx={{ minWidth: 1000 }} aria-label="simple table">
                  <TableHead>
                    <TableRow
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '0.3fr repeat(7, 1fr)',
                      }}
                    >
                      <TableCell>#</TableCell>
                      <S.CustomTableCellHeading align="left">Job Post</S.CustomTableCellHeading>
                      <S.CustomTableCellHeading align="left">Company</S.CustomTableCellHeading>
                      <S.CustomTableCellHeading align="left">Location</S.CustomTableCellHeading>
                      <S.CustomTableCellHeading align="left" style={{ textAlign: 'right' }}>
                        Applicants
                      </S.CustomTableCellHeading>
                      <S.CustomTableCellHeading align="left">Created</S.CustomTableCellHeading>
                      <S.CustomTableCellHeading align="left">Updated</S.CustomTableCellHeading>
                      <S.CustomTableCellHeading align="right">Actions</S.CustomTableCellHeading>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeJobPostsData?.results?.map((row, index) => {
                      return (
                        <TableRow
                          key={row.id}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                            display: 'grid',
                            gridTemplateColumns: '0.3fr repeat(7, 1fr)',
                          }}
                        >
                          <TableCell component="th" scope="row">
                            {index + 1}
                          </TableCell>
                          <S.CustomTableCellJobPost
                            align="left"
                            onClick={async () => {
                              await storageService.setItem('createJobPostId', row?.id);
                              return navigate(
                                `/profile/job-post-preview?type=view&jobPostId=${row?.id}`,
                                {
                                  replace: true,
                                }
                              );
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            {row?.jobTitle || '-'}
                          </S.CustomTableCellJobPost>
                          <S.CustomTableCell align="left">
                            {row?.company?.name || '-'}
                          </S.CustomTableCell>
                          <S.CustomTableCell align="left">
                            {row?.location?.name || '-'}
                          </S.CustomTableCell>
                          <S.CustomTableCell align="right">
                            {row?.counters?.newApplicationsCount > 0 && (
                              <S.NewApplicantsSpan component="span">
                                +{row?.counters?.newApplicationsCount}
                              </S.NewApplicantsSpan>
                            )}
                            <S.ApplicantsSpan
                              component="span"
                              onClick={() => {
                                navigate(
                                  `/candidates/applicants?jobPostId=${row.id}&applicantType=all`
                                );
                              }}
                            >
                              {row?.counters?.applicationsCount}
                            </S.ApplicantsSpan>
                          </S.CustomTableCell>
                          <S.CustomTableCell align="left">
                            {new Date(row.createdAt).toLocaleDateString('en-US', {
                              month: '2-digit',
                              day: '2-digit',
                              year: 'numeric',
                            })}
                          </S.CustomTableCell>
                          <S.CustomTableCell align="left">
                            {new Date(row.updatedAt).toLocaleDateString('en-US', {
                              month: '2-digit',
                              day: '2-digit',
                              year: 'numeric',
                            })}
                          </S.CustomTableCell>
                          <S.CustomTableCell align="right">
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                gap: '12px',
                              }}
                            >
                              <EditGreyIcon
                                style={{ cursor: 'pointer', flexShrink: 0 }}
                                onClick={() => {
                                  storageService.setItem('createJobPostId', row.id).then((res) => {
                                    updateJobPostForEditing({ editStatus: 'edit' }, res);
                                    actions.resetForm();
                                  });
                                }}
                              />
                              <DraftGreyIcon
                                style={{ cursor: 'pointer', flexShrink: 0 }}
                                onClick={() => {
                                  if (update)
                                    update({
                                      isOpenMoveJobPostToDraftModal: true,
                                      jobPostIdToDraft: row.id,
                                    });
                                }}
                              />
                              <DeleteRedIcon
                                style={{ cursor: 'pointer', flexShrink: 0 }}
                                onClick={() => {
                                  if (update)
                                    update({
                                      isOpenDeleteJobPostModal: true,
                                      jobPostIdToDelete: row.id,
                                    });
                                }}
                              />
                            </div>
                          </S.CustomTableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </S.CandidatesListBlockMainWrapper>
              <CS.CustomPagination
                count={Math.ceil(activeJobPostsData.total / JOB_POSTS_ITEMS_PER_PAGE)}
                shape="rounded"
                page={page}
                onChange={(e, page) => {
                  setPage(page);
                  getActiveJobPosts(page);
                }}
                style={{ margin: 'auto 0 0 auto' }}
              />
            </>
          ) : null}
          <DeleteJobPostModal successFunction={deleteActivePostSuccessFunction} />
          <MoveJobPostToDraftModal successFunction={moveJobPostToDraftSuccessFunction} />
        </S.MainContentWrapper>
      )}
      {postStatusFromUrl === 'draft' && (
        <S.MainContentWrapper>
          {isDraftsJobPostsLoading && (
            <Spinner
              variant="outlined"
              style={{
                position: 'absolute',
                top: '0',
                left: '0',
                backgroundColor: 'rgba(217, 225, 255, 0.3)',
                width: '100%',
                height: '100%',
                zIndex: 10,
              }}
            />
          )}
          {!isDraftsJobPostsLoading && !draftsJobPostsData?.total && (
            <NoResultsBlock
              title="✏️ No Drafts Yet"
              subTitle={
                'You haven’t saved any job drafts. Start a post and \n save it here to finish later.'
              }
              withButton
            />
          )}
          {draftsJobPostsData?.total && !!draftsJobPostsData?.results?.length ? (
            <>
              <S.CandidatesListBlockMainWrapper>
                <Table sx={{ minWidth: 1000, overflow: 'auto' }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <S.CustomTableCellHeading align="left">Job Post</S.CustomTableCellHeading>
                      <S.CustomTableCellHeading align="left">Company</S.CustomTableCellHeading>
                      <S.CustomTableCellHeading align="left">Location</S.CustomTableCellHeading>
                      <S.CustomTableCellHeading align="left" style={{ textAlign: 'right' }}>
                        Applicants
                      </S.CustomTableCellHeading>
                      <S.CustomTableCellHeading align="left">Created</S.CustomTableCellHeading>
                      <S.CustomTableCellHeading align="left">Updated</S.CustomTableCellHeading>
                      <S.CustomTableCellHeading align="right">Actions</S.CustomTableCellHeading>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {draftsJobPostsData?.results?.map((row, index) => {
                      const isMoveToActiveDisabled =
                        !row?.industry ||
                        !row?.jobCategory ||
                        !row?.jobTitle ||
                        !row?.positionType?.length ||
                        !row?.experienceYearsMax ||
                        !row?.experienceYearsMin ||
                        !row?.rolesAndResponsibilities ||
                        !row?.company?.id ||
                        !row?.applicationProcess;
                      return (
                        <TableRow
                          key={row.id}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell component="th" scope="row">
                            {index + 1}
                          </TableCell>
                          <S.CustomTableCellJobPost
                            align="left"
                            onClick={async () => {
                              await storageService.setItem('createJobPostId', row?.id);
                              return navigate(
                                `/profile/job-post-preview?type=view&jobPostId=${row?.id}`,
                                {
                                  replace: true,
                                }
                              );
                            }}
                            style={{ cursor: 'pointer' }}
                          >
                            {row?.jobTitle || '-'}
                          </S.CustomTableCellJobPost>
                          <S.CustomTableCell align="left">
                            {row?.company?.name || '-'}
                          </S.CustomTableCell>
                          <S.CustomTableCell align="left">
                            {row?.location?.name || '-'}
                          </S.CustomTableCell>
                          <S.CustomTableCell align="right">
                            {row?.counters?.newApplicationsCount > 0 && (
                              <S.NewApplicantsSpan component="span">
                                +{row?.counters?.newApplicationsCount}
                              </S.NewApplicantsSpan>
                            )}
                            <S.ApplicantsSpan
                              component="span"
                              onClick={() => {
                                navigate(
                                  `/candidates/applicants?jobPostId=${row.id}&applicantType=all`
                                );
                              }}
                            >
                              {row?.counters?.applicationsCount}
                            </S.ApplicantsSpan>
                          </S.CustomTableCell>
                          <S.CustomTableCell align="left">
                            {new Date(row.createdAt).toLocaleDateString('en-US', {
                              month: '2-digit',
                              day: '2-digit',
                              year: 'numeric',
                            })}
                          </S.CustomTableCell>
                          <S.CustomTableCell align="left">
                            {new Date(row.updatedAt).toLocaleDateString('en-US', {
                              month: '2-digit',
                              day: '2-digit',
                              year: 'numeric',
                            })}
                          </S.CustomTableCell>
                          <S.CustomTableCell align="right">
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                alignItems: 'center',
                                gap: '12px',
                              }}
                            >
                              <EditGreyIcon
                                style={{ cursor: 'pointer', flexShrink: 0 }}
                                onClick={() => {
                                  storageService.setItem('createJobPostId', row.id).then((res) => {
                                    updateJobPostForEditing({ editStatus: 'edit' }, res);
                                    actions.resetForm();
                                  });
                                }}
                              />
                              <DraftRestoreGreyIcon
                                style={{
                                  cursor: isMoveToActiveDisabled ? 'not-allowed' : 'pointer',
                                  flexShrink: 0,
                                  pointerEvents: isMoveToActiveDisabled ? 'none' : 'initial',
                                  opacity: isMoveToActiveDisabled ? '0.5' : '1',
                                }}
                                /* fill={isMoveToActiveDisabled ? 'grey' : null} */
                                onClick={() => {
                                  if (update)
                                    update({
                                      isOpenMoveJobPostToActiveModal: true,
                                      jobPostIdToActive: row.id,
                                    });
                                }}
                              />
                              <DeleteRedIcon
                                style={{ cursor: 'pointer', flexShrink: 0 }}
                                onClick={() => {
                                  if (update)
                                    update({
                                      isOpenDeleteJobPostModal: true,
                                      jobPostIdToDelete: row.id,
                                    });
                                }}
                              />
                            </div>
                          </S.CustomTableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </S.CandidatesListBlockMainWrapper>
              <CS.CustomPagination
                count={Math.ceil(draftsJobPostsData.total / JOB_POSTS_ITEMS_PER_PAGE)}
                shape="rounded"
                page={draftsPage}
                onChange={(e, page) => {
                  setDraftsPage(page);
                  getDraftsJobPosts(page);
                }}
                style={{ margin: 'auto 0 0 auto' }}
              />
            </>
          ) : null}
          <DeleteJobPostModal successFunction={deleteDraftPostSuccessFunction} />
          <MoveJobPostToActiveModal successFunction={moveJobPostToActiveSuccessFunction} />
        </S.MainContentWrapper>
      )}
    </S.Wrapper>
  );
};

export default MyJobPostsPage;
